import type { ReactNode, SyntheticEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useCollections, useUsersList } from "../api/queries";
import { useSearch, type DateFilter, type SearchSort, type SearchStatus } from "../api/search";
import { useAuth } from "../auth/auth-context";
import { PageHeader } from "../layout/PageHeader";
import { cx } from "../lib/cx";
import { Snippet } from "./snippet";

const PAGE_SIZE = 25;

const STATUSES: { id: SearchStatus; label: string }[] = [
  { id: "published", label: "Published" },
  { id: "draft", label: "Drafts" },
  { id: "archived", label: "Archived" },
];

const DATES: { id: DateFilter; label: string }[] = [
  { id: "day", label: "Past day" },
  { id: "week", label: "Past week" },
  { id: "month", label: "Past month" },
  { id: "year", label: "Past year" },
];

const SORTS: { id: SearchSort; label: string }[] = [
  { id: "relevance", label: "Relevance" },
  { id: "updatedAt", label: "Recently updated" },
  { id: "createdAt", label: "Recently created" },
  { id: "title", label: "Title" },
];

function isStatus(value: string): value is SearchStatus {
  return STATUSES.some((status) => status.id === value);
}

function isDate(value: string): value is DateFilter {
  return DATES.some((date) => date.id === value);
}

function isSort(value: string): value is SearchSort {
  return SORTS.some((sort) => sort.id === value);
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "rounded-full border px-3 py-1 text-xs",
        active ? "border-accent bg-accent text-on-accent" : "border-line text-muted hover:bg-hover",
      )}
    >
      {label}
    </button>
  );
}

/**
 * `/search` and `/search/:query`. Every control is in the URL — the term, the
 * chips, the sort and the page — so a search is a link somebody can send
 * (note 03 §3.2).
 */
export function SearchScreen(): ReactNode {
  const { query: pathQuery } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status } = useAuth();

  const term = pathQuery ?? searchParams.get("query") ?? searchParams.get("q") ?? "";
  const collectionId = searchParams.get("collectionId") ?? undefined;
  const userId = searchParams.get("userId") ?? undefined;
  const dateParam = searchParams.get("dateFilter") ?? "";
  const dateFilter = isDate(dateParam) ? dateParam : undefined;
  const statusFilter = searchParams.getAll("statusFilter").filter(isStatus);
  const titlesOnly = searchParams.get("titleFilter") === "true";
  const sortParam = searchParams.get("sort") ?? "";
  const sort: SearchSort = isSort(sortParam) ? sortParam : "relevance";
  const direction = searchParams.get("direction") === "ASC" ? "ASC" : "DESC";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;

  const collections = useCollections(status === "authenticated");
  const users = useUsersList({ limit: 100, offset: 0 }, status === "authenticated");

  const results = useSearch({
    query: term,
    ...(collectionId === undefined ? {} : { collectionId }),
    ...(userId === undefined ? {} : { userId }),
    ...(dateFilter === undefined ? {} : { dateFilter }),
    ...(statusFilter.length === 0 ? {} : { statusFilter }),
    sort,
    direction,
    titlesOnly,
    limit: PAGE_SIZE,
    offset,
  });

  /** Every control writes the URL; the URL is the only state this screen has. */
  const setParam = (changes: Record<string, string | string[] | undefined>): void => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      next.delete(key);
      if (Array.isArray(value)) for (const entry of value) next.append(key, entry);
      else if (value !== undefined) next.set(key, value);
    }
    if (!("offset" in changes)) next.delete("offset");
    setSearchParams(next, { replace: true });
  };

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("query");
    const trimmed = typeof value === "string" ? value.trim() : "";
    const suffix = searchParams.toString();
    void navigate(
      trimmed === ""
        ? "/search"
        : `/search/${encodeURIComponent(trimmed)}${suffix === "" ? "" : `?${suffix}`}`,
    );
  };

  const collectionName = (id: string | null | undefined): string | undefined =>
    id == null ? undefined : (collections.data ?? []).find((entry) => entry.id === id)?.name;

  const total = results.data?.total ?? 0;
  const items = results.data?.items ?? [];

  return (
    <>
      <PageHeader>
        <h1 className="truncate text-base font-semibold">Search</h1>
      </PageHeader>

      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">Search</h2>

        <form role="search" className="mt-4" key={term} onSubmit={submit}>
          <label className="flex flex-col gap-1 text-sm">
            Search terms
            <input
              type="search"
              name="query"
              defaultValue={term}
              placeholder="Search…"
              className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
            />
          </label>
        </form>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {STATUSES.map((entry) => (
            <Chip
              key={entry.id}
              label={entry.label}
              active={statusFilter.includes(entry.id)}
              onClick={() => {
                setParam({
                  statusFilter: statusFilter.includes(entry.id)
                    ? statusFilter.filter((value) => value !== entry.id)
                    : [...statusFilter, entry.id],
                });
              }}
            />
          ))}
          {DATES.map((entry) => (
            <Chip
              key={entry.id}
              label={entry.label}
              active={dateFilter === entry.id}
              onClick={() => {
                setParam({ dateFilter: dateFilter === entry.id ? undefined : entry.id });
              }}
            />
          ))}
          <Chip
            label="Titles only"
            active={titlesOnly}
            onClick={() => {
              setParam({ titleFilter: titlesOnly ? undefined : "true" });
            }}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted">
            Collection
            <select
              value={collectionId ?? ""}
              onChange={(event) => {
                setParam({
                  collectionId: event.target.value === "" ? undefined : event.target.value,
                });
              }}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm text-ink"
            >
              <option value="">Any collection</option>
              {(collections.data ?? []).map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-muted">
            Person
            <select
              value={userId ?? ""}
              onChange={(event) => {
                setParam({ userId: event.target.value === "" ? undefined : event.target.value });
              }}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm text-ink"
            >
              <option value="">Anyone</option>
              {(users.data?.users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-muted">
            Sort
            <select
              value={sort}
              onChange={(event) => {
                setParam({ sort: event.target.value });
              }}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm text-ink"
            >
              {SORTS.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-muted">
            Direction
            <select
              value={direction}
              onChange={(event) => {
                setParam({ direction: event.target.value });
              }}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm text-ink"
            >
              <option value="DESC">Descending</option>
              <option value="ASC">Ascending</option>
            </select>
          </label>
        </div>

        <ul
          aria-label="Search results"
          aria-busy={results.isFetching}
          className="mt-6 divide-y divide-line rounded-md border border-line"
        >
          {term.trim() === "" ? (
            <li className="px-4 py-6 text-sm text-muted">Type something to search for.</li>
          ) : results.isError ? (
            <li role="alert" className="px-4 py-6 text-sm text-danger">
              {results.error.message}
            </li>
          ) : results.isPending ? (
            <li className="px-4 py-6 text-sm text-muted">Searching…</li>
          ) : items.length === 0 ? (
            <li className="px-4 py-6 text-sm text-muted">Nothing matches “{term}”.</li>
          ) : (
            items.map((item) => (
              <li key={item.document.id} className="px-4 py-3">
                <Link to={item.document.url} className="text-sm font-medium hover:underline">
                  {item.document.title === "" ? "Untitled" : item.document.title}
                </Link>
                <p className="mt-0.5 text-xs text-muted">
                  {collectionName(item.document.collectionId) ?? "Drafts"}
                </p>
                {item.context !== undefined && <Snippet context={item.context} />}
              </li>
            ))
          )}
        </ul>

        {total > PAGE_SIZE && (
          <nav aria-label="Search pagination" className="mt-3 flex items-center gap-3 text-sm">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => {
                setParam({ offset: String(Math.max(0, offset - PAGE_SIZE)) });
              }}
              className="rounded-md border border-line px-2 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-muted">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => {
                setParam({ offset: String(offset + PAGE_SIZE) });
              }}
              className="rounded-md border border-line px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </>
  );
}
